import React, { useState, useEffect, useMemo } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import { ToastContainer, toast } from 'react-toastify';
import axios from "axios";
import * as XLSX from 'xlsx';
import excelIcon from "../../assets/icons/excel.png";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FaFilePdf } from "react-icons/fa";

import AppDataGrid from '../../components/common/AppDataGrid';
import { Tooltip } from '@mui/material';

const HomeworkReport = () => {
  const userData = sessionStorage.getItem('user');
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [academic, setAcademic] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [filteredSections, setFilteredSections] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const userObj = userData ? JSON.parse(userData) : {};
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const [form, setForm] = useState({
    class_id: "",
    section_id: "",
    subject_id: "",
    date: ""
  });

  const searchableColumns = ["class_name", "section_name", "subject_name"];

  const columns = [
    {
      field: 'class_name',
      headerName: 'Class',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value ?? ''}><span>{params.value ?? ''}</span></Tooltip>
      ),
    },
    {
      field: 'section_name',
      headerName: 'Section',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value ?? ''}><span>{params.value ?? ''}</span></Tooltip>
      ),
    },
    {
      field: 'subject_name',
      headerName: 'Subject',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value ?? ''}>
          <span style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
            {params.value ?? ''}
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'homework_date',
      headerName: 'Homework Date',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value ?? ''}><span>{params.value ?? ''}</span></Tooltip>
      ),
    },
    {
      field: 'submit_date',
      headerName: 'Submit Date',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value ?? ''}><span>{params.value ?? ''}</span></Tooltip>
      ),
    },
    {
      field: 'submitted_count',
      headerName: 'Submitted Count',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={String(params.value ?? '')}><span>{params.value ?? ''}</span></Tooltip>
      ),
    },
    {
      field: 'pending_count',
      headerName: 'Pending Count',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={String(params.value ?? '')}><span>{params.value ?? ''}</span></Tooltip>
      ),
    },
  ];

  const fetchClasses = async () => {
    try {
      const response = await axios.post(baseUrl + "/classes/", {
        action: "READ",
        school_id: userObj?.school_id || 0,
        academic_year_id: userObj.academic_year_id
      });
      setClasses(response?.data?.filter(classItem => classItem.is_active === "Active") || []);
    } catch (error) {
      console.error("Error fetching classes!", error);
    }
  };

  const fetchSections = async (class_id) => {
    try {
      const response = await axios.post(baseUrl + "/Sections/", {
        action: "READ",
        school_id: userObj.school_id,
        class_id,
        academic_year_id: userObj.academic_year_id
      });
      const data = Array.isArray(response.data) ? response.data : [];
      const active = data.filter((item) => item.is_active?.toLowerCase() === "active");
      setSections(active);
      setFilteredSections(active);
    } catch (err) {
      console.error("Error fetching sections", err);
    }
  };

  const fetchAcademicYears = async () => {
    try {
      const response = await axios.post(baseUrl + "/AcademicYear/", {
        action: "CURRENTREAD",
        school_id: userObj?.school_id || 0,
      });
      setAcademic(response?.data || []);
    } catch (error) {
      console.error("Error fetching academic years!", error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await axios.post(baseUrl + "/teacherssubjectsmap/", {
        action: "READ",
        school_id: userObj?.school_id || 0,
        academic_year_id: userObj.academic_year_id
      });
      // ✅ keep full list in state, we will filter based on class/section in UI
      setSubjects(response?.data?.filter(subjectItem => subjectItem.is_active === "Active") || []);
    } catch (error) {
      console.error("Error fetching subjects!", error);
    }
  };

  const fetchData = async () => {
    try {
      // ✅ Don't send empty string; some APIs ignore/override it
      const params = {
        action: "GET",
        school_id: userObj.school_id,
        class_id: form.class_id ? Number(form.class_id) : 0,
        section_id: form.section_id ? Number(form.section_id) : 0,
        subject_id: form.subject_id ? Number(form.subject_id) : 0,
        academic_year_id: userObj.academic_year_id,

        // ✅ send BOTH keys to support backend variants
        homework_date: form.date ? form.date : undefined,
        date: form.date ? form.date : undefined,
      };

      const res = await axios.get(baseUrl + "/main/get_homework_report", { params });
      const data = res?.data?.data || [];
      setReportData(data);
    } catch (err) {
      toast.error("Error fetching report data.");
    }
  };

  useEffect(() => {
    fetchAcademicYears();
    fetchClasses();
    fetchData();
    fetchSubjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e) => {
    const { id, value } = e.target;

    setForm((prevForm) => ({
      ...prevForm,
      [id]: value,

      // ✅ when class changes: reset section + subject
      ...(id === "class_id" && { section_id: "", subject_id: "" }),

      // ✅ when section changes: reset subject
      ...(id === "section_id" && { subject_id: "" }),
    }));

    if (id === "class_id") fetchSections(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await fetchData();
  };

  const exportToExcel = () => {
    const exportData = reportData.map(row => ({
      Class: row.class_name,
      Section: row.section_name,
      Subject: row.subject_name,
      "Homework Date": row.homework_date,
      "Submit Date": row.submit_date,
      "Submitted Count": row.submitted_count,
      "Pending Count": row.pending_count
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Homework Report");
    XLSX.writeFile(wb, "homework_report.xlsx");
  };

  const exportToPDF = () => {
    if (reportData.length === 0) {
      toast.error("No data available to export.");
      return;
    }

    const doc = new jsPDF();
    doc.text("Homework Report", 14, 10);

    const tableColumn = ["Class", "Section", "Subject", "Homework Date", "Submit Date", "Submitted", "Pending"];
    const tableRows = reportData.map(row => [
      row.class_name,
      row.section_name,
      row.subject_name,
      row.homework_date,
      row.submit_date,
      row.submitted_count,
      row.pending_count
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [0, 123, 255] },
    });

    doc.save(`homework_report_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  // ✅ Subject dropdown: filter by class + section (from teacherssubjectsmap)
  const filteredSubjects = useMemo(() => {
    const classId = form.class_id ? Number(form.class_id) : 0;
    const sectionId = form.section_id ? Number(form.section_id) : 0;

    let list = [...subjects];

    if (classId) list = list.filter(s => Number(s.class_id) === classId);
    if (sectionId) list = list.filter(s => Number(s.section_id) === sectionId);

    // ✅ remove duplicates by subject_id (same subject may appear for multiple teachers etc.)
    const map = new Map();
    list.forEach(item => {
      if (!map.has(item.subject_id)) map.set(item.subject_id, item);
    });
    return Array.from(map.values());
  }, [subjects, form.class_id, form.section_id]);

  const filteredRows = reportData.length
    ? reportData.filter(row =>
        searchableColumns.some(col =>
          (row[col] || "").toString().toLowerCase().includes(searchQuery.toLowerCase())
        )
      )
    : [];

  const getRowId = (row) =>
    row.id ??
    `${row.class_name || ''}-${row.section_name || ''}-${row.subject_name || ''}-${row.homework_date || ''}`;

  return (
    <div className='pageBody'>
      <ToastContainer />
      <Container fluid>
        <Card>
          <Card.Body>
            <form onSubmit={handleSubmit}>
              <Row>
                <Col xs={12}>
                  <h6 className='commonSectionTitle'>Homework Report</h6>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6} lg={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Class</Form.Label>
                      <Form.Select
                        id="class_id"
                        value={form.class_id}
                        onChange={handleInputChange}
                      >
                        <option value="" disabled hidden>Select Class</option>
                        {classes.map(c => (
                          <option key={c.class_id} value={c.class_id}>{c.class_name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Section</Form.Label>
                      <Form.Select
                        id="section_id"
                        value={form.section_id}
                        onChange={handleInputChange}
                        disabled={!form.class_id} // ✅ optional: prevents selecting section before class
                      >
                        <option value="" disabled hidden>Select Section</option>
                        {filteredSections
                          .filter(s => s.class_id === parseInt(form.class_id))
                          .map(s => (
                            <option key={s.section_id} value={s.section_id}>{s.section_name}</option>
                          ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Subject</Form.Label>
                      <Form.Select
                        id="subject_id"
                        value={form.subject_id}
                        onChange={handleInputChange}
                        disabled={!form.class_id || !form.section_id} // ✅ force class+section first
                      >
                        <option value="" disabled hidden>Select Subject</option>
                        {filteredSubjects.map(s => (
                          <option key={s.subject_id} value={s.subject_id}>{s.subject_name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Date</Form.Label>
                      <Form.Control
                        type="date"
                        id="date"
                        value={form.date}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </div>
                </Col>
              </Row>

              <div className="d-flex justify-content-end mt-4">
                <Button
                  type="button"
                  variant="secondary"
                  className="btn-info clearBtn me-3"
                  onClick={() => {
                    setForm({ class_id: "", section_id: "", subject_id: "", date: "" });
                    setReportData([]);
                  }}
                >
                  Reset
                </Button>
                <Button type="submit" variant="success" className="btn btn-success primaryBtn">Search</Button>
              </div>
            </form>
          </Card.Body>
        </Card>

        <div className="d-flex justify-content-between align-items-center mt-3">
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            className="form-control form-control-sm w-25"
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <div>
            <Button className="btnMain" variant="success" onClick={exportToExcel} style={{ marginRight: "10px" }}>
              <img src={excelIcon} alt="Excel" style={{ width: "20px" }} />
            </Button>
            <Button className="btnMain" variant="danger" onClick={exportToPDF}>
              <FaFilePdf style={{ marginRight: "5px" }} />
            </Button>
          </div>
        </div>

        <div className="commonTable height100 mt-2">
          <div className="tableBody">
            <AppDataGrid
              rows={filteredRows.length ? filteredRows : []}
              columns={columns}
              getRowId={getRowId}
              pageSizeOptions={[5, 10, 15]}
              showToolbar={false}
              pagination
              density="compact"
              maxHeight={undefined}
              slots={{
                noRowsOverlay: () => (
                  <div style={{ padding: 16, textAlign: 'center', color: 'red' }}>
                    No Data Available
                  </div>
                ),
              }}
            />
          </div>
        </div>
      </Container>
    </div>
  );
};

export default HomeworkReport;
